/**
 * Shared User resolution for login + AuthContext.
 * Must match org/staff routing in App.tsx ensureUserRecord / checkUserProfile.
 */

export const STAFF_ROLES_FOR_LOGIN = new Set([
  'ORG_ADMIN',
  'THERAPIST',
  'CLINICIAN',
  'INTERNS',
  'ADMIN',
  'SME',
  'SUPER_ADMIN',
]);

export type LoginUserRow = {
  id: string;
  email?: string | null;
  role?: string | null;
  organizationId?: string | null;
  lastLogin?: string | null;
};

type UserListClient = {
  models: {
    User: {
      list: (args: {
        limit?: number;
        nextToken?: string | undefined;
        authMode?: 'apiKey' | 'userPool';
      }) => Promise<{ data?: LoginUserRow[] | null; nextToken?: string | null }>;
    };
  };
};

/**
 * Paginated email lookup; prefers staff roles (avoids stopping on first PARENT in an early page).
 */
export async function findBestUserRowByEmail(
  dataClient: UserListClient,
  emailLower: string
): Promise<LoginUserRow | null> {
  if (!emailLower) return null;
  const matches: LoginUserRow[] = [];
  let nextToken: string | null | undefined = undefined;
  try {
    do {
      const res = await dataClient.models.User.list({
        limit: 1000,
        nextToken: nextToken || undefined,
        authMode: 'apiKey',
      });
      for (const u of res.data || []) {
        if (u?.email?.toLowerCase() === emailLower) matches.push(u);
      }
      const staffHit = matches.find((u) => u.role && STAFF_ROLES_FOR_LOGIN.has(String(u.role)));
      if (staffHit) return staffHit;
      nextToken = res.nextToken ?? null;
    } while (nextToken);
  } catch {
    return null;
  }
  const preferredByRole = matches.find((u) => u.role && u.role !== 'PARENT');
  const preferredByOrg = matches.find((u) => u.organizationId);
  return preferredByRole || preferredByOrg || matches[0] || null;
}

/** All User rows for an email (paginated). Used to merge lastLogin across duplicate accounts. */
export async function findAllUserRowsByEmail(
  dataClient: UserListClient,
  emailLower: string,
): Promise<LoginUserRow[]> {
  if (!emailLower) return [];
  const matches: LoginUserRow[] = [];
  let nextToken: string | null | undefined = undefined;
  try {
    do {
      const res = await dataClient.models.User.list({
        limit: 1000,
        nextToken: nextToken || undefined,
        authMode: 'apiKey',
      });
      for (const u of res.data || []) {
        if (u?.email?.toLowerCase() === emailLower) matches.push(u);
      }
      nextToken = res.nextToken ?? null;
    } while (nextToken);
  } catch {
    return matches;
  }
  return matches;
}

/** Merge Cognito-linked row with email-scanned row (same rules as App ensureUserRecord). */
export function pickUserRecordForSession(
  cognitoRow: LoginUserRow | null | undefined,
  emailRow: LoginUserRow | null
): LoginUserRow | null {
  if (!emailRow) return cognitoRow ?? null;
  if (!cognitoRow) return emailRow;
  const cognitoStaff = cognitoRow.role ? STAFF_ROLES_FOR_LOGIN.has(String(cognitoRow.role)) : false;
  const emailStaff = emailRow.role ? STAFF_ROLES_FOR_LOGIN.has(String(emailRow.role)) : false;
  if (emailStaff && (!cognitoRow.role || cognitoRow.role === 'PARENT' || !cognitoStaff)) {
    return emailRow;
  }
  return cognitoRow;
}
